import { useCallback, useEffect, useState } from 'react'
import { Box, Button, Chip, IconButton, Paper, TextField, Typography, useTheme } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from 'src/contexts/AuthContext'
import { addSeatsToSubscription, createGroupCheckoutSession, formatAddSeatsSummary, syncGroupSubscriptionFromStripe } from '@batical/common'
import { doc } from 'firebase/firestore'
import { firestore } from 'src/shims/firebase'
import { useDocumentData } from 'react-firebase-hooks/firestore'
import { useSelector } from 'react-redux'
import { selectSubscription, selectUserId } from '@batical/reducer/src'
import type { GroupSubscriber } from '@batical/common'
import { getSiteOrigin } from 'src/utils/siteUrl'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'

type SubscriberRow = GroupSubscriber & { id: string }

const emptyRow = (): SubscriberRow => ({
  id: crypto.randomUUID(),
  mail: '',
  firstname: '',
  lastname: '',
  company: ''
})

export function AddTeamPage() {
  const navigate = useNavigate()
  const theme = useTheme()
  const { user } = useAuth()
  const uid = useSelector(selectUserId) ?? user?.uid ?? ''
  const [rows, setRows] = useState<SubscriberRow[]>(() => [emptyRow()])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()

  const [stripeUser] = useDocumentData(doc(firestore, 'stripe', uid))
  const [stripeDefault] = useDocumentData(doc(firestore, 'stripe', 'default'))
  const rawPriceId = (stripeUser as any)?.priceId ?? (stripeDefault as any)?.priceId
  const effectivePriceId: string | undefined = Array.isArray(rawPriceId) ? rawPriceId[0] : rawPriceId

  const subscription = useSelector(selectSubscription)
  const [groupData, groupDataLoading] = useDocumentData(doc(firestore, 'subscription', uid, 'groupSubscription', 'data'))
  const hasExistingSubscription = Boolean(groupData?.stripeSubscriptionId) && groupData?.status === 'active'

  // Sync Firestore ← Stripe si abonné Stripe mais doc groupSubscription absent
  const hasActiveStripe = (subscription.expirationDateStripe ?? 0) > Math.floor(Date.now() / 1000)
  useEffect(() => {
    if (!uid || groupDataLoading) return
    const hasGroupSubId = Boolean((groupData as any)?.stripeSubscriptionId)
    if (hasActiveStripe && !hasGroupSubId) {
      syncGroupSubscriptionFromStripe().catch((e) => console.error('sync failed', e))
    }
  }, [uid, groupDataLoading, groupData, hasActiveStripe])

  const addRow = useCallback(() => setRows((r) => [...r, emptyRow()]), [])

  const updateRow = useCallback((id: string, field: keyof SubscriberRow, value: string) => {
    setRows((r) => r.map((row) => (row.id === id ? { ...row, [field]: value } : row)))
  }, [])

  const removeRow = useCallback((id: string) => {
    setRows((r) => (r.length <= 1 ? r : r.filter((row) => row.id !== id)))
  }, [])

  const collectSubscribers = useCallback((): GroupSubscriber[] => {
    return rows
      .map((r) => ({
        mail: (r.mail ?? '').trim(),
        firstname: (r.firstname ?? '').trim() || undefined,
        lastname: (r.lastname ?? '').trim() || undefined,
        company: (r.company ?? '').trim() || undefined
      }))
      .filter((s) => s.mail)
  }, [rows])

  const handleSubmit = useCallback(async () => {
    const subscribers = collectSubscribers()
    console.log('[AddTeamPage] handleSubmit → entry', {
      uid,
      subscribers,
      hasExistingSubscription,
      effectivePriceId,
      groupData,
      stripeUser,
      stripeDefault
    })
    if (subscribers.length === 0) {
      setError('Ajoutez au moins un email.')
      return
    }

    setError(undefined)
    setLoading(true)

    try {
      if (hasExistingSubscription) {
        console.log('[AddTeamPage] addSeatsToSubscription → calling', { subscribers })
        const result = await addSeatsToSubscription(subscribers)
        console.log('[AddTeamPage] addSeatsToSubscription ← OK', result)
        const seatSummary = formatAddSeatsSummary(result)
        const hadSeatChange = (result.seatsPurchased ?? 0) + (result.seatsReused ?? 0) > 0
        navigate('/team', {
          state: {
            seatSummary,
            pendingMembers: hadSeatChange ? subscribers : undefined
          }
        })
      } else {
        // No subscription yet — create a new checkout session
        if (!effectivePriceId) {
          console.warn('[AddTeamPage] ABORT — no priceId', { stripeUser, stripeDefault })
          setError('Configuration Stripe manquante (priceId).')
          setLoading(false)
          return
        }
        const payload = {
          ownerUid: uid,
          baseUrl: getSiteOrigin(),
          priceId: effectivePriceId,
          subscribers
        }
        console.log('[AddTeamPage] createGroupCheckoutSession → calling', payload)
        const { url } = await createGroupCheckoutSession(payload)
        console.log('[AddTeamPage] createGroupCheckoutSession ← OK', { url })
        window.location.href = url
      }
    } catch (err: unknown) {
      console.error('[AddTeamPage] handleSubmit ← ERR', err)
      const e = err as { message?: string; code?: string; details?: string }
      const msg =
        e?.message && e.message !== 'INTERNAL'
          ? e.message
          : typeof e?.details === 'string'
            ? e.details
            : 'Erreur lors de la soumission.'
      setError(msg)
      setLoading(false)
    }
  }, [collectSubscribers, hasExistingSubscription, uid, effectivePriceId, navigate])

  const validCount = rows.filter((r) => r.mail?.trim()).length

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.5,
      '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(66,119,185,0.08)' }
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50' }}>
          Ajouter des membres
        </Typography>
        {hasExistingSubscription && (
          <Chip label="Abonnement actif" size="small" color="success" variant="outlined" sx={{ fontWeight: 600 }} />
        )}
      </Box>
      <Typography variant="body1" sx={{ color: '#7f8c8d', mb: 3 }}>
        {hasExistingSubscription
          ? 'Les nouveaux membres seront ajoutés à votre abonnement existant (facturation au prorata).'
          : 'Saisissez les emails des personnes qui bénéficieront d\'un abonnement. Vous serez redirigé vers Stripe pour le paiement.'}
      </Typography>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
        }}>
        {rows.map((row, idx) => (
          <Box
            key={row.id}
            sx={{
              display: 'flex',
              gap: 1.5,
              mb: idx < rows.length - 1 ? 2 : 0,
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
            <TextField label="Email *" type="email" size="small" value={row.mail} onChange={(e) => updateRow(row.id, 'mail', e.target.value)} sx={{ ...fieldSx, minWidth: 220, flex: 2 }} />
            <TextField label="Prénom" size="small" value={row.firstname} onChange={(e) => updateRow(row.id, 'firstname', e.target.value)} sx={{ ...fieldSx, minWidth: 120, flex: 1 }} />
            <TextField label="Nom" size="small" value={row.lastname} onChange={(e) => updateRow(row.id, 'lastname', e.target.value)} sx={{ ...fieldSx, minWidth: 120, flex: 1 }} />
            <TextField label="Entreprise" size="small" value={row.company} onChange={(e) => updateRow(row.id, 'company', e.target.value)} sx={{ ...fieldSx, minWidth: 140, flex: 1 }} />
            <IconButton
              onClick={() => removeRow(row.id)}
              disabled={rows.length <= 1}
              size="small"
              sx={{ color: rows.length > 1 ? theme.palette.error.main : 'text.disabled' }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}

        <Button
          onClick={addRow}
          startIcon={<AddCircleOutlineIcon />}
          size="small"
          sx={{ mt: 2, fontWeight: 600, textTransform: 'none', color: theme.palette.primary.main }}>
          Ajouter une ligne
        </Button>
      </Paper>

      {error && (
        <Typography color="error" sx={{ mb: 2, fontWeight: 500 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || (!hasExistingSubscription && !effectivePriceId)}
          disableElevation
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            textTransform: 'none',
            px: 4,
            py: 1.2,
            backgroundColor: '#4277b9',
            '&:hover': { backgroundColor: '#3666a3' }
          }}>
          {loading
            ? (hasExistingSubscription ? 'Ajout en cours…' : 'Redirection vers Stripe…')
            : (hasExistingSubscription
              ? `Ajouter ${validCount} membre(s)`
              : `Payer pour ${validCount} membre(s)`)}
        </Button>
        <Button
          onClick={() => navigate('/team')}
          sx={{ fontWeight: 600, textTransform: 'none', color: '#7f8c8d' }}>
          Annuler
        </Button>
      </Box>

      {!hasExistingSubscription && !effectivePriceId && (
        <Typography variant="body2" sx={{ color: '#7f8c8d', mt: 3, fontStyle: 'italic' }}>
          Aucun tarif Stripe configuré. Ajoutez un document Firestore <code>stripe/default</code> avec un champ <code>priceId</code>.
        </Typography>
      )}
    </>
  )
}
